import type { ComponentType } from "react";
import { useSearchParams } from "react-router-dom";

export interface TabDef {
  id: string;
  label: string;
  /** Optional leading glyph. `BrandIntelligence`'s tabs have none; `Profile`'s do. */
  icon?: ComponentType<{ className?: string }>;
}

export interface TabParam {
  /** The requested tab when it is one this page actually has, else `defaultTab`. */
  activeTab: string;
  /** An href for `tab`, preserving every other search param. */
  tabHref: (tab: string) => string;
}

/**
 * Reads the `?tab=` param that three pages use to address a sub-view, and builds the hrefs that
 * make those sub-views openable in a new tab (AUT-256 for `BrandIntelligence`, AUT-399 for
 * `Profile`). Extracted because both pages had this verbatim, alongside a copy of the tab strip
 * whose hover styling carries a specificity trap -- see `LinkTabs`. Two copies of a documented
 * trap is how one of them silently stops matching the other.
 *
 * Deliberately no `{ replace: true }`: Back should return to the previously viewed tab, the way
 * any other link behaves.
 *
 * `Settings` is not a caller. Its sections are cards rather than a tab strip, and its href has to
 * clear the param to return to the index, which this does not do.
 */
export function useTabParam(tabs: readonly TabDef[], defaultTab: string): TabParam {
  const [searchParams] = useSearchParams();

  const requested = searchParams.get("tab");
  const activeTab = tabs.some((t) => t.id === requested) ? (requested as string) : defaultTab;

  const tabHref = (tab: string): string => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", tab);

    return `?${next.toString()}`;
  };

  return { activeTab, tabHref };
}
